import { string, func, array, bool, node, number } from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { renderCheck } from 'ravenjs/utils/viewport';
import { callFunc } from 'ravenjs/utils/actions';
import TooltipNew from 'ravenjs/lib/TooltipNew';
import { COLORS } from 'ravenjs/constants/colors';

import { FILTER_OVERLAY_OPEN_EVENT } from 'constants/navigation';
import { nextOverlayInstanceId, supportsOverlayEvents } from 'utils/overlayInstanceId';

const ContainerIconStyled = styled.div`
    display: ${({ display }) => display};
    cursor: pointer;
`;

const MainContainer = styled.div`
    display: ${({ display }) => display || undefined};
    margin: ${({ margin }) => margin || 0};
    width: ${({ width }) => width};
    text-overflow: ${({ hasEllipsis }) => (hasEllipsis ? 'ellipsis' : '')};
    overflow: ${({ hasEllipsis }) => (hasEllipsis ? 'hidden' : '')};
`;

function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
}

const ContentTooltipPreview = props => {
    const {
        checkIconChange,
        display,
        color,
        borderColor,
        borderRadius,
        content,
        displayRefContainer,
        extraClass,
        fontWeight,
        hasEllipsis,
        hideInnerContent,
        hideTooltip,
        icon,
        margin,
        offset,
        onMouseEnter,
        onMouseLeave,
        placement,
        width,
        boxShadow,
        widthContainer,
        open,
        applyNewTooltipStyle,
        textColor,
        backgroundColor,
        isTooltipContent,
        closeDelay,
    } = props;

    const isTouchDevice = 'ontouchstart' in window || navigator.msMaxTouchPoints;
    const prevIcon = usePrevious(icon);
    const iconRef = useRef();
    const parentContainerRef = useRef();
    const closeDelayTimeoutRef = useRef(null);
    const instanceIdRef = useRef(null);
    if (instanceIdRef.current === null) {
        instanceIdRef.current = nextOverlayInstanceId('content-tooltip-overlay');
    }
    const [show, setShow] = useState(false);
    const isHoveringRef = useRef(false);

    const cancelPendingClose = () => {
        if (closeDelayTimeoutRef.current) {
            clearTimeout(closeDelayTimeoutRef.current);
            closeDelayTimeoutRef.current = null;
        }
    };

    const handleTooltipContentEnter = () => {
        isHoveringRef.current = true;
        cancelPendingClose();
    };

    React.useEffect(
        () => () => {
            if (closeDelayTimeoutRef.current) {
                clearTimeout(closeDelayTimeoutRef.current);
                closeDelayTimeoutRef.current = null;
            }
        },
        []
    );

    React.useEffect(() => {
        if (closeDelay <= 0 || !supportsOverlayEvents(globalThis)) {
            return undefined;
        }

        const handleOtherOverlayOpen = event => {
            if (event?.detail?.source === instanceIdRef.current) return;
            if (closeDelayTimeoutRef.current) {
                clearTimeout(closeDelayTimeoutRef.current);
                closeDelayTimeoutRef.current = null;
            }
            setShow(false);
        };

        globalThis.addEventListener(FILTER_OVERLAY_OPEN_EVENT, handleOtherOverlayOpen);
        return () => {
            globalThis.removeEventListener(FILTER_OVERLAY_OPEN_EVENT, handleOtherOverlayOpen);
        };
    }, [closeDelay]);

    React.useEffect(() => {
        if (checkIconChange && prevIcon && prevIcon !== icon) {
            setShow(false);
        }
    }, [checkIconChange, icon, prevIcon]);

    React.useEffect(() => {
        const handleScroll = event => {
            // Don't close if mouse is hovering over the overlay (same as avatar menu behavior)
            if (isHoveringRef.current) {
                return;
            }
            if (parentContainerRef.current?.contains(event.target)) {
                return;
            }
            const portalTooltip = document.getElementById('containerTooltip');
            if (portalTooltip?.contains(event.target)) {
                return;
            }
            setShow(false);
        };

        window?.addEventListener('scroll', handleScroll, true);

        return () => {
            window?.removeEventListener('scroll', handleScroll, true);
        };
    }, []);

    const handleMouseEnter = () => {
        isHoveringRef.current = true;
        cancelPendingClose();
        setShow(true);

        if (closeDelay > 0 && supportsOverlayEvents(globalThis)) {
            globalThis.dispatchEvent(
                new CustomEvent(FILTER_OVERLAY_OPEN_EVENT, {
                    detail: { source: instanceIdRef.current },
                })
            );
        }

        if (typeof onMouseEnter === 'function') {
            callFunc(onMouseEnter);
        }
    };

    const handleMouseLeave = () => {
        isHoveringRef.current = false;
        if (!hideInnerContent && !isTouchDevice) {
            if (closeDelay > 0) {
                cancelPendingClose();
                closeDelayTimeoutRef.current = setTimeout(() => {
                    closeDelayTimeoutRef.current = null;
                    setShow(false);
                }, closeDelay);
            } else {
                setShow(false);
            }

            if (typeof onMouseLeave === 'function') {
                callFunc(onMouseLeave);
            }
        }
    };

    // Close-on-outside-click for mobile/touch. Registered while the tooltip is
    // shown so the handler always reflects the latest props (prevents a stale
    // `onMouseLeave` from committing outdated values), and cleaned up on close.
    React.useEffect(() => {
        if (!show) {
            return undefined;
        }

        const handleOutsideClick = e => {
            const tooltipContainer = document.getElementById('containerTooltip');
            if (!tooltipContainer) {
                return;
            }
            e.stopImmediatePropagation();
            const isMobile = renderCheck('md', 'less');
            // Use the instance's own trigger ref instead of a shared DOM id so
            // multiple tooltips on the same page don't clash (duplicate ids).
            const clickedTrigger = iconRef.current?.contains(e.target);
            const clickedTooltip = tooltipContainer.contains(e.target);

            if ((isMobile || isTouchDevice) && !clickedTrigger && !clickedTooltip) {
                if (isTouchDevice) {
                    callFunc(onMouseLeave);
                }

                setShow(false);
            }
        };

        window.addEventListener('click', handleOutsideClick);

        return () => {
            window.removeEventListener('click', handleOutsideClick);
        };
    }, [show, onMouseLeave, isTouchDevice]);

    // A click outside the trigger/tooltip closes right away instead of waiting
    // out the close delay, matching the navigation menus. Opt-in via `closeDelay`
    // so tooltips without a delay keep their original behavior.
    React.useEffect(() => {
        if (closeDelay <= 0 || !show) {
            return undefined;
        }

        const handleOutsideMouseDown = e => {
            if (iconRef.current?.contains(e.target)) {
                return;
            }
            const tooltipContainer = document.getElementById('containerTooltip');
            if (tooltipContainer?.contains(e.target)) {
                return;
            }
            if (closeDelayTimeoutRef.current) {
                clearTimeout(closeDelayTimeoutRef.current);
                closeDelayTimeoutRef.current = null;
            }
            setShow(false);
        };

        document.addEventListener('mousedown', handleOutsideMouseDown);
        return () => {
            document.removeEventListener('mousedown', handleOutsideMouseDown);
        };
    }, [closeDelay, show]);

    React.useEffect(() => {
        if (typeof open === 'boolean') {
            setShow(open);
        }
    }, [open]);

    return (
        <MainContainer
            display={display}
            margin={margin}
            width={widthContainer}
            hasEllipsis={hasEllipsis}
        >
            {content || hideInnerContent ? (
                <div
                    ref={parentContainerRef}
                    data-testid="parentTooltipContainer"
                    id="parentTooltipContainer"
                    onMouseEnter={handleMouseEnter}
                    onPointerLeave={handleMouseLeave}
                    onTouchStart={handleMouseEnter}
                >
                    <ContainerIconStyled
                        id="triggerTooltip"
                        className={extraClass}
                        display={displayRefContainer}
                        ref={iconRef}
                    >
                        {icon}
                    </ContainerIconStyled>
                    {content && show && !isTooltipContent && content}
                    {content && show && isTooltipContent && (
                        <TooltipNew
                            id="containerTooltip"
                            borderColor={borderColor}
                            borderRadius={borderRadius}
                            color={color}
                            fontWeight={fontWeight}
                            hideTooltip={hideTooltip}
                            offset={offset}
                            placement={placement}
                            trigger={iconRef.current}
                            width={width}
                            boxShadow={boxShadow}
                            applyNewTooltipStyle={applyNewTooltipStyle}
                            textColor={textColor}
                            backgroundColor={backgroundColor}
                        >
                            {closeDelay > 0 ? (
                                <div
                                    data-testid="tooltipContentWrapper"
                                    onMouseEnter={handleTooltipContentEnter}
                                    onPointerLeave={handleMouseLeave}
                                >
                                    {content}
                                </div>
                            ) : (
                                content
                            )}
                        </TooltipNew>
                    )}
                </div>
            ) : (
                <ContainerIconStyled>{icon}</ContainerIconStyled>
            )}
        </MainContainer>
    );
};

ContentTooltipPreview.propTypes = {
    checkIconChange: bool,
    display: string,
    color: string,
    borderColor: string,
    borderRadius: string,
    textColor: string,
    backgroundColor: string,
    content: node,
    displayRefContainer: string,
    extraClass: string,
    fontWeight: string,
    hasEllipsis: bool,
    hideInnerContent: bool,
    hideTooltip: bool,
    icon: node,
    margin: string,
    offset: array,
    open: bool,
    onMouseEnter: func,
    onMouseLeave: func,
    placement: string,
    width: string,
    boxShadow: string,
    widthContainer: string,
    applyNewTooltipStyle: bool,
    isTooltipContent: bool,
    closeDelay: number,
};

ContentTooltipPreview.defaultProps = {
    checkIconChange: false,
    display: null,
    color: COLORS.WHITE,
    borderColor: COLORS.BLACK,
    textColor: null,
    backgroundColor: null,
    borderRadius: '0.25rem',
    content: null,
    displayRefContainer: 'flex',
    extraClass: '',
    fontWeight: '',
    hasEllipsis: false,
    hideInnerContent: false,
    hideTooltip: false,
    icon: null,
    margin: '0',
    offset: [0, 8],
    open: null,
    onMouseEnter: null,
    onMouseLeave: null,
    placement: 'left',
    width: '',
    boxShadow: '',
    widthContainer: '',
    applyNewTooltipStyle: true,
    isTooltipContent: true,
    closeDelay: 0,
};

export default ContentTooltipPreview;
